import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { requestAudioTranscription } from "@/hooks/audioTranscriptPipelineService";
import { buildAudioUploadArtifacts, deriveRecordedAudioFormat, uploadWithProgress } from "@/hooks/audioTranscriptPipelineHelpers";
import { useReflectionRecorder } from "@/hooks/useReflectionRecorder";
import type { CoachVeeraDictationStatus } from "@/components/chat/CoachVeeraComposer";

type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: unknown) => void) | null;
  onerror: ((event: unknown) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};

type SpeechRecognitionConstructorLike = new () => SpeechRecognitionLike;

function getSpeechRecognitionConstructor(): SpeechRecognitionConstructorLike | null {
  if (typeof window === "undefined") return null;
  const candidate =
    (window as Window & { SpeechRecognition?: SpeechRecognitionConstructorLike }).SpeechRecognition ??
    (window as Window & { webkitSpeechRecognition?: SpeechRecognitionConstructorLike }).webkitSpeechRecognition;
  return candidate ?? null;
}

function mergeTranscript(currentValue: string, transcript: string) {
  const trimmedCurrent = currentValue.trim();
  const trimmedTranscript = transcript.trim();
  if (!trimmedTranscript) return trimmedCurrent;
  if (!trimmedCurrent) return trimmedTranscript;
  return `${trimmedCurrent} ${trimmedTranscript}`;
}

function readSpeechRecognitionTranscript(event: unknown) {
  const results = (event as { results?: ArrayLike<ArrayLike<{ transcript?: string }>> })?.results;
  if (!results) return "";

  const chunks: string[] = [];
  for (const result of Array.from(results)) {
    const transcript = result?.[0]?.transcript;
    if (transcript) {
      chunks.push(transcript);
    }
  }

  return chunks.join(" ").trim();
}

function getSpeechRecognitionErrorMessage(event: unknown) {
  const error = (event as { error?: string })?.error;
  if (error === "not-allowed" || error === "service-not-allowed") {
    return "Microphone access was blocked.";
  }
  if (error === "no-speech") {
    return "We couldn't hear anything to transcribe.";
  }
  return "Voice dictation couldn't start.";
}

async function transcribeRecordedAudio(audioBlob: Blob, actorId: string) {
  const { audioFile, audioPath } = buildAudioUploadArtifacts({
    fileName: "coach-veera-dictation",
    audioBlob,
    audioFormat: deriveRecordedAudioFormat(audioBlob),
    leaderId: actorId,
    meetingId: undefined,
    uuid: crypto.randomUUID(),
  });

  await uploadWithProgress({
    bucket: "transcript_audio",
    path: audioPath,
    file: audioFile,
    onProgress: () => undefined,
  });

  const transcript = await requestAudioTranscription({
    audioPath,
    leaderId: actorId,
    retainAudioForProcessing: false,
  });

  if (!transcript || !transcript.trim()) {
    throw new Error("We couldn't transcribe that voice note.");
  }

  return transcript.trim();
}

type DictationProvider = "native" | "fallback" | null;

export function useCoachVeeraDictation({
  currentValue,
  onValueChange,
}: {
  currentValue: string;
  onValueChange: (value: string) => void;
}) {
  const { user } = useAuth();
  const { startRecording, stopRecording } = useReflectionRecorder();
  const [status, setStatus] = useState<CoachVeeraDictationStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const speechRecognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const speechTranscriptRef = useRef("");
  const speechStopRequestedRef = useRef(false);
  const speechRecognitionHadErrorRef = useRef(false);
  const providerRef = useRef<DictationProvider>(null);
  const currentValueRef = useRef(currentValue);

  useEffect(() => {
    currentValueRef.current = currentValue;
  }, [currentValue]);

  const applyTranscript = useCallback((transcript: string) => {
    const nextValue = mergeTranscript(currentValueRef.current, transcript);
    if (!nextValue) return;
    onValueChange(nextValue);
  }, [onValueChange]);

  const speechRecognitionConstructor = useMemo(() => getSpeechRecognitionConstructor(), []);
  const supported = useMemo(() => {
    if (speechRecognitionConstructor) return true;
    if (typeof navigator === "undefined") return false;
    return typeof navigator.mediaDevices?.getUserMedia === "function" && typeof MediaRecorder !== "undefined";
  }, [speechRecognitionConstructor]);

  const resetToIdle = useCallback(() => {
    providerRef.current = null;
    setStatus("idle");
  }, []);

  const start = useCallback(async () => {
    setError(null);

    if (speechRecognitionConstructor) {
      providerRef.current = "native";
      speechTranscriptRef.current = "";
      speechStopRequestedRef.current = false;
      speechRecognitionHadErrorRef.current = false;
      const recognition = new speechRecognitionConstructor();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = "en-US";
      recognition.onresult = (event) => {
        speechTranscriptRef.current = readSpeechRecognitionTranscript(event);
      };
      recognition.onerror = (event) => {
        speechRecognitionHadErrorRef.current = true;
        setError(getSpeechRecognitionErrorMessage(event));
        providerRef.current = null;
        speechRecognitionRef.current = null;
        setStatus("error");
      };
      recognition.onend = () => {
        const transcript = speechTranscriptRef.current.trim();
        speechRecognitionRef.current = null;
        if (speechRecognitionHadErrorRef.current) {
          providerRef.current = null;
          return;
        }
        if (!transcript) {
          if (!speechStopRequestedRef.current) {
            providerRef.current = null;
            setStatus("idle");
            return;
          }
          setError("We couldn't hear anything to transcribe.");
          providerRef.current = null;
          setStatus("error");
          return;
        }

        applyTranscript(transcript);
        resetToIdle();
      };
      speechRecognitionRef.current = recognition;
      try {
        recognition.start();
        setStatus("recording");
      } catch (caughtError) {
        speechRecognitionHadErrorRef.current = true;
        providerRef.current = null;
        speechRecognitionRef.current = null;
        setError(caughtError instanceof Error ? caughtError.message : "Voice dictation couldn't start.");
        setStatus("error");
      }
      return;
    }

    try {
      providerRef.current = "fallback";
      const result = await startRecording();
      if (result.permissionDenied) {
        setError("Microphone access was blocked.");
        providerRef.current = null;
        setStatus("error");
        return;
      }

      setStatus("recording");
    } catch (caughtError) {
      providerRef.current = null;
      setError(caughtError instanceof Error ? caughtError.message : "Voice dictation couldn't start.");
      setStatus("error");
    }
  }, [applyTranscript, resetToIdle, speechRecognitionConstructor, startRecording]);

  const stop = useCallback(async () => {
    setError(null);

    if (providerRef.current === "native") {
      speechStopRequestedRef.current = true;
      speechRecognitionRef.current?.stop();
      return;
    }

    if (providerRef.current !== "fallback") return;

    setStatus("transcribing");
    try {
      const audioBlob = await stopRecording();
      if (!audioBlob) {
        throw new Error("We couldn't capture your voice note.");
      }

      const actorId = user?.id;
      if (!actorId) {
        throw new Error("You need to be signed in to use voice dictation.");
      }

      const transcript = await transcribeRecordedAudio(audioBlob, actorId);
      applyTranscript(transcript);
      resetToIdle();
    } catch (caughtError) {
      providerRef.current = null;
      setError(caughtError instanceof Error ? caughtError.message : "Voice dictation failed.");
      setStatus("error");
    }
  }, [applyTranscript, resetToIdle, stopRecording, user?.id]);

  const cancel = useCallback(() => {
    providerRef.current = null;
    speechStopRequestedRef.current = false;
    speechRecognitionHadErrorRef.current = false;
    speechRecognitionRef.current = null;
    setError(null);
    setStatus("idle");
  }, []);

  return {
    supported,
    status,
    error,
    start,
    stop,
    cancel,
  };
}
